import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useStore, useQuery } from '../../../../store/mst';
import { PlanningActionsStatus, RouteDtoModelType } from '../../../../store/mst/models';
import getRouteColors from './../helpers/getRouteColors';
import SelectDriverView from './SelectDriverView';
import { ErrorText } from './SelectDriver.styles';
import AnalyticsEvents from '../../../../analytics/analyticsEvents';
import useSubscribeToDriverChanges from '../../../../hooks/useSubscribeToDriverChanges';
interface Props {
  route: RouteDtoModelType;
  setPopupVisible: (visible: boolean) => void;
}

const SelectDriver: React.FC<Props> = ({
  route,
  setPopupVisible,
}) => {
  const { t } = useTranslation('components');
  const store = useStore();
  const { subscription, model, ui, sortedRoutes } = store;

  const queryDrivers = () => model.getDriversForAssignmentToRoute(route.uuid as string);
  const { loading, data: driversData, setQuery } = useQuery(queryDrivers);

  const [assignDriverError, setAssignDriverError] = useState<string>('');
  const [isPending, setIsPending] = useState<boolean>(false);

  const sortedDrivers = store.sortedDriversForAssignmentToRoute(
    driversData?.getDriversForAssignmentToRoute || [],
  );

  // Refetch drivers when subscription events come in
  const refetchDrivers = useCallback(() => {
    setQuery(queryDrivers());
  }, [setQuery]);

  useSubscribeToDriverChanges(refetchDrivers);

  // Intentionally only re-runs when route.driver changes — subscription and t are stable references
  useEffect(() => {
    subscription.startDriverSubscription();
    if (route.driver?.deleted) {
      setAssignDriverError(t('assignDriver.labels.driverDeleted'));
    }
    return () => subscription.stopDriverSubscription();
  }, [route.driver]);

  const currentWorkspaceId = ui.selectedWorkspace?.id as string;

  const assignDriver = async (driverId: string) => {
    setAssignDriverError('');
    setIsPending(true);

    try {
      const response: any = await model.assignDriverToRoute(currentWorkspaceId, route.uuid as string, driverId);

      if (response.assignDriverToRoute?.status === PlanningActionsStatus.FAILED) {
        setAssignDriverError(t('assignDriver.notifications.error'));
        return;
      }
      const driverIndex = sortedDrivers.findIndex((d) => d.id === driverId);
      if (driverIndex !== -1) {
        const driver = sortedDrivers[driverIndex];
        store.account.trackUserForAnalytics(AnalyticsEvents.ASSIGN_DRIVER_TO_ROUTE, {
          routeId: route.uuid as string,
          driverId: driver.uuid as string,
          routeStatus: route.isDispatched ? 'published' : 'planned',
          driverSpeed: driver.speed || undefined,
          driverFamiliarityScore: driver.familiarityScore?.score,
          driverFamiliarityRating: driver.familiarityScore?.rating,
          driverFamiliarityRanking: driver.familiarityScore ? driverIndex + 1 : undefined,
          availableDrivers: sortedDrivers.length,
        });
      }
      store.model.refreshCalendarRoutes();
      setPopupVisible(false);
    } catch (error) {
      setAssignDriverError(t('assignDriver.notifications.error'));
    } finally {
      setIsPending(false);
    }
  };

  const routeColorsByDriverId = sortedDrivers.reduce<Record<string, any>>((acc, d) => {
    acc[d.id] = getRouteColors(sortedRoutes, d.id);
    return acc;
  }, {});


  return (
    <>
      <SelectDriverView
        route={route}
        drivers={sortedDrivers}
        loading={loading}
        isPending={isPending}
        assignDriver={assignDriver}
        routeColorsByDriverId={routeColorsByDriverId}
    />
      {assignDriverError && (
      <ErrorText type="danger" data-cy="assign-driver-error">
        {assignDriverError}
      </ErrorText>
      )}
    </>
  );
};

export default observer(SelectDriver);
